import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconCopy, IconPencil, IconTrash, IconExclamationMark, IconCircleCheck } from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import ActionCellRender from "../../components/actionCellRender";
import {
  PageList,
  ENUM_FORM_DISPLAY,
} from "../../components/pageList";
import UserRoleAccessImportModal from "../../components/userRoleAccessImportModal";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { useAccessRight } from "../../hooks/useAccessRight";
import { buildRoleEditSearch } from "../../helpers/userRoleDuplicate";
import { subscribeListRefresh, notifyListRefresh } from "../../context/tabContext";

const apiEntity = "userRole";
const ROLE_EDIT_PATH = "/operation/userRole/edit";
const RETURN_PATH = "/operation/userRole";

export const indexRoleUserCounts = (groups) => {
  const counts = {};
  (groups ?? []).forEach((group) => {
    if (!group?._id) return;
    counts[String(group._id)] = group.count ?? 0;
  });
  return counts;
};

const formSchema = {
  title: "User Role",
  apiEntity,
  display: {
    mode: ENUM_FORM_DISPLAY.INSIDE,
    size: "xl",
  },
  layout: {
    containers: [],
    fields: [],
  },
};

const UserRole = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const tabNav = useTabNavigate();
  const [, effectiveAccessRight] = useAccessRight();
  const [counts, setCounts] = useState({});
  const [openedImport, setOpenedImport] = useState(false);
  const canWrite = effectiveAccessRight === 15;

  const loadCounts = useCallback(async () => {
    try {
      const groups = await apiRef.current.User.getListGroupByRole();
      setCounts(indexRoleUserCounts(groups));
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    loadCounts();
    return subscribeListRefresh(RETURN_PATH, loadCounts);
  }, [loadCounts]);

  const openEditor = useCallback(
    (params, label) => {
      if (!canWrite) return;
      tabNav.open({
        pathname: ROLE_EDIT_PATH,
        search: buildRoleEditSearch(params, RETURN_PATH),
        label,
      });
    },
    [canWrite, tabNav]
  );

  const handleDelete = useCallback(
    async (role) => {
      if (!canWrite) return;
      const count = counts[String(role._id)] ?? role.count ?? 0;
      if (count > 0) {
        showNotification({
          title: t("Delete Fail"),
          message: t(
            "This role has {{count}} user(s). Remove or reassign users before deleting.",
            { count }
          ),
          color: "red",
          icon: <IconExclamationMark size={18} />,
        });
        return;
      }

      const roleLabel = [role.name, role.cname].filter(Boolean).join(" ");
      if (
        !window.confirm(
          t('Are you sure you want to delete role "{{name}}"?', {
            name: roleLabel,
          })
        )
      ) {
        return;
      }

      const result = await apiRef.current.removeById({ apiEntity, id: role._id });
      if (!result?.success) return;

      showNotification({
        title: t("Delete Success"),
        message: t("Role deleted successfully."),
        color: "green",
        icon: <IconCircleCheck size={18} />,
      });
      notifyListRefresh(RETURN_PATH);
    },
    [canWrite, counts, t]
  );

  const tableSchema = useMemo(
    () => ({
      showActionColumn: canWrite,
      disableRowDoubleClick: !canWrite,
      select: "name cname description status",
      searchableFields: ["name", "cname", "description", "status"],
      onRowDoubleClick: ({ data }) => {
        openEditor({ id: data._id, mode: "edit" }, t("User Role"));
      },
      onActionBtnClickCustom: ({ action, data }) => {
        if (action === "edit") {
          openEditor({ id: data._id, mode: "edit" }, t("User Role"));
          return;
        }
        if (action === "duplicate") {
          openEditor(
            { mode: "add", copyFrom: data._id },
            t("New User Role")
          );
          return;
        }
        if (action === "delete") {
          handleDelete(data);
        }
      },
      columns: [
        {
          field: "name",
          headerName: "English Name",
          sortable: true,
        },
        {
          field: "cname",
          headerName: "Chinese Name",
          sortable: true,
        },
        {
          field: "description",
          headerName: "Description",
        },
        {
          field: "userCount",
          headerName: "Users",
          cellRender: (_column, data) => counts[String(data._id)] ?? 0,
        },
        {
          field: "status",
          headerName: "Status",
          sortable: true,
          cellRender: (_column, data) => data.status || "ACTIVE",
        },
        ...(canWrite
          ? [
              {
                field: "action",
                cellRender: ActionCellRender,
                isAction: true,
                actions: [
                  {
                    name: "edit",
                    label: "Edit",
                    icon: IconPencil,
                  },
                ],
                actionMenu: [
                  {
                    name: "duplicate",
                    label: "Duplicate Role",
                    icon: IconCopy,
                  },
                  {
                    name: "delete",
                    label: "Delete Role",
                    icon: IconTrash,
                    props: { color: "red" },
                  },
                ],
              },
            ]
          : []),
      ],
    }),
    [canWrite, counts, handleDelete, openEditor, t]
  );

  const customActions = useMemo(
    () => [
      {
        label: t("New User Role"),
        requiresWrite: true,
        onClick: () => openEditor({ mode: "add" }, t("New User Role")),
      },
      {
        label: t("Import Access Right"),
        requiresWrite: true,
        onClick: () => setOpenedImport(true),
      },
    ],
    [openEditor, t]
  );

  return (
    <>
      <PageList
        title={t("User Role")}
        apiEntity={apiEntity}
        tableSchema={tableSchema}
        formSchema={formSchema}
        showAddButton={false}
        customActions={customActions}
        initSort={{ by: "name", order: "asc" }}
      />
      <UserRoleAccessImportModal
        opened={openedImport}
        onClose={() => setOpenedImport(false)}
        onImported={() => notifyListRefresh(RETURN_PATH)}
      />
    </>
  );
};

export default UserRole;
